import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { env } from '@noor/config';
import { toast } from '@noor/lib';
import { sessionApi } from '@/entities/session';
import { conversationsKeys } from '@/entities/conversation';
import { playChatNotificationSound } from '../lib/browser-notification';

const CHAT_DISABLED_ROLES = ['AF_MANAGER', 'AF_ADMIN'] as const;

interface FirebaseMessagePayload {
  notification?: { title?: string; body?: string };
  data?: { chat_id?: string };
}

function readChatId(raw: string | undefined): number | undefined {
  if (!raw) return undefined;
  const id = Number(raw);
  return Number.isFinite(id) ? id : undefined;
}

function wasPermissionAsked(): boolean {
  try {
    return localStorage.getItem('notification_permission_asked') === 'true';
  } catch {
    return false;
  }
}

function markPermissionAsked(): void {
  try {
    localStorage.setItem('notification_permission_asked', 'true');
  } catch {
    // Browser storage may be disabled; permission state itself remains authoritative.
  }
}

/** Register FCM once the signed-in user enables notifications in the browser. */
export function useChatPushNotifications(role: string | null, enabled: boolean): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    const normalizedRole = role?.toUpperCase() ?? '';
    if (
      !enabled ||
      !role ||
      env.VITE_ENABLE_MSW ||
      !env.VITE_FIREBASE_API_KEY ||
      !env.VITE_FIREBASE_PROJECT_ID ||
      !env.VITE_FIREBASE_MESSAGING_SENDER_ID ||
      !env.VITE_FIREBASE_APP_ID ||
      !env.VITE_FIREBASE_VAPID_KEY ||
      (CHAT_DISABLED_ROLES as readonly string[]).includes(normalizedRole) ||
      typeof window === 'undefined' ||
      typeof Notification === 'undefined' ||
      !('serviceWorker' in navigator)
    ) {
      return;
    }

    let disposed = false;
    let unsubscribe: (() => void) | undefined;
    let messagingPromise: Promise<void> | undefined;

    const registerMessaging = (): Promise<void> => {
      if (messagingPromise) return messagingPromise;

      messagingPromise = (async () => {
        try {
          const [
            { getApps, initializeApp },
            { getMessaging, getToken, isSupported, onMessage },
          ] = await Promise.all([import('firebase/app'), import('firebase/messaging')]);
          if (disposed || !(await isSupported())) return;

          const config = {
            apiKey: env.VITE_FIREBASE_API_KEY,
            authDomain: env.VITE_FIREBASE_AUTH_DOMAIN,
            projectId: env.VITE_FIREBASE_PROJECT_ID,
            storageBucket: env.VITE_FIREBASE_STORAGE_BUCKET,
            messagingSenderId: env.VITE_FIREBASE_MESSAGING_SENDER_ID,
            appId: env.VITE_FIREBASE_APP_ID,
            measurementId: env.VITE_FIREBASE_MEASUREMENT_ID,
          };
          if (
            !config.apiKey ||
            !config.projectId ||
            !config.messagingSenderId ||
            !config.appId ||
            !env.VITE_FIREBASE_VAPID_KEY
          ) {
            return;
          }

          const app =
            getApps().find((candidate) => candidate.name === 'cpanel-chat-push') ??
            initializeApp(config, 'cpanel-chat-push');
          const messaging = getMessaging(app);
          const messagingScope = new URL('/fcm/', window.location.origin).href;
          const registrations = await navigator.serviceWorker.getRegistrations();
          const registration = registrations.find(
            (candidate) => candidate.scope === messagingScope,
          );
          if (!registration) return;
          if (disposed || Notification.permission !== 'granted') return;

          const token = await getToken(messaging, {
            vapidKey: env.VITE_FIREBASE_VAPID_KEY,
            serviceWorkerRegistration: registration,
          });
          if (disposed || !token) return;

          await sessionApi.saveFirebaseToken(token);
          if (disposed) return;

          unsubscribe = onMessage(messaging, (payload) => {
            const notification = (payload as FirebaseMessagePayload).notification;
            const data = (payload as FirebaseMessagePayload).data;
            const title = notification?.title ?? '';
            const body = notification?.body ?? '';
            if (title || body) toast.info([title, body].filter(Boolean).join(': '));
            playChatNotificationSound();

            const chatId = readChatId(data?.chat_id);
            void queryClient.invalidateQueries({ queryKey: conversationsKeys.lists() });
            if (chatId !== undefined) {
              void queryClient.invalidateQueries({
                queryKey: conversationsKeys.thread(chatId),
              });
              void queryClient.invalidateQueries({
                queryKey: conversationsKeys.detail(chatId),
              });
            }
          });
        } catch {
          // Push is best-effort; socket chat updates continue to work without it.
        }
      })();

      return messagingPromise;
    };

    const onServiceWorkerMessage = (event: MessageEvent<{ type?: string }>) => {
      if (event.data?.type === 'PLAY_NOTIFICATION_SOUND') playChatNotificationSound();
    };
    navigator.serviceWorker.addEventListener('message', onServiceWorkerMessage);

    if (Notification.permission === 'granted') {
      void registerMessaging();
    } else if (Notification.permission === 'default' && !wasPermissionAsked()) {
      const askOnFirstInteraction = () => {
        void Notification.requestPermission()
          .then((permission) => {
            markPermissionAsked();
            if (permission === 'granted') void registerMessaging();
          })
          .catch(markPermissionAsked);
      };
      document.addEventListener('click', askOnFirstInteraction, { once: true });
      return () => {
        disposed = true;
        document.removeEventListener('click', askOnFirstInteraction);
        navigator.serviceWorker.removeEventListener('message', onServiceWorkerMessage);
        unsubscribe?.();
      };
    }

    return () => {
      disposed = true;
      navigator.serviceWorker.removeEventListener('message', onServiceWorkerMessage);
      unsubscribe?.();
    };
  }, [enabled, queryClient, role]);
}
